import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { IdNegocio } from '../operaciones';
import type { components } from '../tipos/clientes';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Importacion = Esquema<'Import'>;
export type ListaImportaciones = Esquema<'ImportList'>;
export type LineaImportacion = Esquema<'ImportLine'>;
export type ResultadoLinea = Esquema<'ImportLineOutcome'>;
export type ResumenImportacion = Esquema<'ImportSummary'>;
export type PruebaPermiso = Esquema<'ImportUpload'>['consent_evidence'];

/** Lo que se sube con el archivo (N4, G202): con «Sí, tengo la prueba», de dónde viene el permiso y cuándo. */
export interface SubidaImportacion {
    archivo: Blob;
    nombre: string;
    prueba: PruebaPermiso;
    /** «Casilla en mi web» (obligatorio con `declared`, salvo que cada línea traiga su columna). */
    origen?: string;
    /** `AAAA-MM-DD` («2024» = 2024-01-01). */
    fecha?: string;
}

const ruta = (businessId: IdNegocio, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = (valor: string) => encodeURIComponent(valor);

/**
 * Operaciones de «Importar clientes» (2.6), con su `operationId` y los tipos generados de `clientes.yaml`. La subida es
 * `multipart/form-data` (el cliente de la API manda el FormData tal cual) y la plantilla, texto CSV. Importar nunca lleva
 * «Deshacer» (G159): descartar y confirmar van con ventana.
 */
export function operacionesClientesImportar(api: ClienteApi) {
    return {
        /** getCustomerImportTemplate (`clientes.importaciones.gestionar`): CSV con las columnas fijas y los campos propios (G149). */
        getCustomerImportTemplate: (businessId: IdNegocio) => api.pedir<string>('GET', ruta(businessId, '/customer-import-template'), { comoTexto: true }),
        /** listCustomerImports (`clientes.importaciones.gestionar`): también la migración desde la v1. */
        listCustomerImports: (businessId: IdNegocio, pagina: Pagina = {}) => api.pedir<ListaImportaciones>('GET', ruta(businessId, `/customer-imports${consulta({ ...pagina })}`)),
        /** createCustomerImport (`clientes.importaciones.gestionar`): 202, validando; no importa nada hasta confirmar. */
        createCustomerImport: (businessId: IdNegocio, subida: SubidaImportacion, clave = claveIdempotencia()) => {
            const formulario = new FormData();
            formulario.append('file', subida.archivo, subida.nombre);
            formulario.append('consent_evidence', subida.prueba);
            if (subida.prueba === 'declared') {
                if (subida.origen !== undefined && subida.origen !== '') {
                    formulario.append('consent_origin', subida.origen);
                }
                if (subida.fecha !== undefined && subida.fecha !== '') {
                    formulario.append('consent_date', subida.fecha);
                }
            }
            return api.pedir<Importacion>('POST', ruta(businessId, '/customer-imports'), { cuerpo: formulario, idempotencia: clave });
        },
        /** getCustomerImport (`clientes.importaciones.gestionar`): resumen y líneas, filtradas por resultado. */
        getCustomerImport: (businessId: IdNegocio, importId: string, resultado?: ResultadoLinea) =>
            api.pedir<Importacion>('GET', ruta(businessId, `/customer-imports/${id(importId)}${consulta({ outcome: resultado })}`)),
        /** confirmCustomerImport (`clientes.importaciones.gestionar`): 202, importando. */
        confirmCustomerImport: (businessId: IdNegocio, importId: string, clave = claveIdempotencia()) =>
            api.pedir<Importacion>('POST', ruta(businessId, `/customer-imports/${id(importId)}/confirmation`), { idempotencia: clave }),
        /** cancelCustomerImport (`clientes.importaciones.gestionar`): la vista previa pasa a «Descartada» (G149). */
        cancelCustomerImport: (businessId: IdNegocio, importId: string, clave = claveIdempotencia()) =>
            api.pedir<Importacion>('POST', ruta(businessId, `/customer-imports/${id(importId)}/cancellation`), { idempotencia: clave }),
    };
}
